import React from 'react';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { IconTypes } from '../Icon';
import { TooltipIcon } from '../Tooltip';
import { Label, ValidationMessage } from '../Typography';

interface FieldWrapperProps {
  action?: string;
  children: React.ReactNode;
  className?: string;
  hasError?: boolean;
  id?: string;
  indentMessage?: boolean;
  instructions?: string | React.ReactNode;
  label?: string;
  subLabel?: string;
  message?: string;
  onAction?: () => void;
  optional?: boolean;
  props?: Record<string, any>;
  tooltip?: string | React.ReactNode;
  tooltipIconSize?: number;
  tooltipIconMarginLeft?: number;
  width?: number;
  icon?: IconTypes;
  tooltipIcon?: string;
}

const Layout = styled.div<{ width?: number }>`
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: ${({ width }) => (width ? toRem(width) : '100%')};
  margin-bottom: ${toRem(theme.space.lg)};

  &:last-child {
    margin-bottom: 0;
  }
`;

const Header = styled.header`
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  margin-bottom: ${toRem(theme.space.sm)};
`;

const HeaderInner = styled.div`
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
`;

const Feedback = styled.section`
  position: relative;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: baseline;
  width: 100%;
  margin-top: ${toRem(theme.space.sm)};
`;

const StyledMessage = styled(ValidationMessage)`
  text-align: left;
  white-space: pre-wrap;
`;

const Action = styled(ValidationMessage)`
  font-weight: ${theme.font.weight.semiBold};
  text-decoration: underline;
  color: ${theme.color.primary[500]};
  cursor: pointer;
`;

const Instructions = styled(ValidationMessage)<Partial<FieldWrapperProps>>`
  ${({ instructions }) =>
    instructions &&
    css`
      margin-top: ${toRem(theme.space.xs)};
    `}
`;

const TooltipWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const TooltipMessage = styled.p`
  text-align: center;
  max-width: ${toRem(220)};
`;

const FieldWrapper: React.FC<FieldWrapperProps> = ({
  action,
  children,
  className,
  hasError,
  id,
  indentMessage = false,
  instructions,
  label,
  subLabel,
  message,
  onAction,
  optional,
  tooltip,
  tooltipIconSize,
  tooltipIconMarginLeft,
  icon,
  tooltipIcon,
  ...props
}) => (
  <Layout className={className} {...props}>
    {(label || action || icon || instructions) && (
      <Header>
        <HeaderInner>
          {label && (
            <TooltipWrapper>
              <Label data-cy="label">
                <span>{label}</span> {(optional || subLabel) && <span className="optional">{subLabel || '(optional)'}</span>}
              </Label>
              {tooltip && (
                <TooltipIcon id={`field-tooltip-${id}`} place="top" iconSize={tooltipIconSize} iconMarginLeft={tooltipIconMarginLeft}>
                  <TooltipMessage>{tooltip}</TooltipMessage>
                </TooltipIcon>
              )}
            </TooltipWrapper>
          )}
          {action && (
            <Action data-cy="action" onClick={onAction}>
              {action}
            </Action>
          )}
          {icon && (
            <TooltipIcon dataCy="field-icon" id={`icon-tooltip-${id}`} icon={icon} place="top" iconSize={14}>
              <TooltipMessage>{tooltipIcon}</TooltipMessage>
            </TooltipIcon>
          )}
        </HeaderInner>

        {instructions && <Instructions instructions={instructions}>{instructions}</Instructions>}
      </Header>
    )}
    {children}

    {message && (
      <Feedback>
        <StyledMessage data-cy="feedback-message" hasError={hasError} indent={indentMessage}>
          {message}
        </StyledMessage>
      </Feedback>
    )}
  </Layout>
);

export default FieldWrapper;
